import { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import messagesIllustration from "resources/images/messages-illustration.png";
import { MediumCustomIcon } from "../../layer00";
import { EmptyStateCard } from "../../layer01";
import { ConversationCard, CardPaginationBar } from "../../layer20";
import { useGetHighestValueConversationsQuery } from "app/containers/TeamsTab/teamsCheckInApi";
import { useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectSampleUsersInfoById,
} from "app/containers/Global/slice";

const PAGE_SIZE = 3;

const label2State = {
  "needs-support": "info",
  "needs-attention": "error",
} as const;

export type ConversationListProps = {};

export const ConversationList = (_props: ConversationListProps) => {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [page, setPage] = useState(0);

  const {
    data: conversations = [],
    isLoading,
    isSuccess,
  } = useGetHighestValueConversationsQuery({});

  // Collapse once the first load comes back empty; after that the user controls it.
  const autoCollapseChecked = useRef(false);
  useEffect(() => {
    if (!isSuccess || autoCollapseChecked.current) return;
    autoCollapseChecked.current = true;
    if (conversations.length === 0) setOpen(false);
  }, [isSuccess, conversations.length]);

  const sampleUsersById = useAppSelector(selectSampleUsersInfoById);
  const companyUsersById = useAppSelector(selectAllCompanyUsersById);
  const usersById = { ...sampleUsersById, ...companyUsersById };

  const totalPages = Math.max(1, Math.ceil(conversations.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const visibleConversations = conversations.slice(
    safePage * PAGE_SIZE,
    (safePage + 1) * PAGE_SIZE
  );

  const getCardBody = () => {
    if (isLoading) {
      return (
        <Box px="1rem" pb="1rem">
          <Skeleton
            variant="rounded"
            height={96}
            sx={{ borderRadius: "1rem" }}
          />
        </Box>
      );
    }
    if (conversations.length === 0) {
      return (
        <EmptyStateCard
          image={messagesIllustration}
          title="No high value conversations this week"
          body="You have no high value conversations this week to display."
        />
      );
    }
    return (
      <Stack direction="column" rowGap="1rem">
        {visibleConversations.map((conv) => {
          const user = usersById[conv.userAccountId];
          const personName = user
            ? `${user.firstName} ${user.lastName}`
            : "Unknown";
          const conversationState = conv.label
            ? label2State[conv.label]
            : "error";

          return (
            <ConversationCard
              key={conv.threadId}
              userAccountId={conv.userAccountId}
              personName={personName}
              emailAddress={user?.emailAddress}
              comment={conv.explanationOfConcern}
              weeklyPoints={conv.weekAheadNotes ?? []}
              winSpotlight={conv.winSpotlight ?? null}
              issueSpotlight={conv.issueSpotlight ?? null}
              conversationState={conversationState}
              date={conv.dateCompleted}
            />
          );
        })}
      </Stack>
    );
  };

  return (
    <Card>
      <CardHeader
        disableTypography
        className={open ? "border-b border-[#8080801f]" : ""}
        onClick={() => setOpen((prev) => !prev)}
        sx={{ cursor: "pointer" }}
        title={
          <Typography variant="cardTitle">
            Highest value conversations this week
          </Typography>
        }
        action={
          <IconButton sx={{ border: "none" }}>
            <MediumCustomIcon
              icon="CHEVRONUP"
              stroke={theme.palette.secondary.light}
              rotated={!open}
            />
          </IconButton>
        }
      />
      <Collapse in={open}>
        <CardContent
          sx={{
            background: "none",
            paddingLeft: 0,
            paddingRight: 0,
            paddingBottom: 0,
          }}
        >
          {getCardBody()}
          {!isLoading && totalPages > 1 && (
            <CardPaginationBar
              page={safePage}
              totalPages={totalPages}
              onNavigate={setPage}
            />
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};
